interface ExtractedTable {
  columns: string[]
  rows: Record<string, string>[]
}

interface MarkdownTableExtraction {
  tables: ExtractedTable[]
  /** The source text with the extracted tables removed. */
  remainder: string
}

const MAX_TABLE_ROWS = 100
const MAX_TABLE_COLUMNS = 12

/**
 * Upper bound on one stored cell string, mirroring `MAX_TABLE_CELL_STRING_CHARS`
 * in sofia-agent's notebook store. The store rejects the whole save when a cell
 * is longer, so keeping a destination may never push a cell past it.
 */
const MAX_CELL_CHARS = 200

/** A code span contributes its literal text to a stored cell, never its backticks. */
const CODE_SPAN_RE = /`([^`]*)`/g

interface CellLinkMatch {
  /** Offset of the opening `[`. */
  index: number
  /** Characters consumed from `index`, the closing `)` included. */
  length: number
  label: string
  /** Parsed destination; empty when the syntax cannot parse back as a link. */
  destination: string
}

/**
 * Find the next `[label](destination)` in `cell` from `from`, scanning the
 * destination with the same rule the renderer parses with — parentheses survive
 * only when backslash-escaped or balanced, and whitespace ends the destination —
 * so arbitrarily nested parentheses stay a preserved link instead of falling
 * out of the match. Syntax that cannot parse back as a link yields an empty
 * destination and the caller keeps only the label, mirroring the base path's
 * `[^)]*` scan, so unparseable markup never reaches a stored cell raw.
 */
function matchCellLink(cell: string, from: number): CellLinkMatch | null {
  for (
    let start = cell.indexOf("[", from);
    start >= 0;
    start = cell.indexOf("[", start + 1)
  ) {
    const labelEnd = cell.indexOf("]", start + 1)
    if (labelEnd <= start + 1 || cell[labelEnd + 1] !== "(") {
      continue
    }
    const label = cell.slice(start + 1, labelEnd)
    let destination = ""
    let balance = 0
    let sawWhitespace = false
    let unterminatedEnd = -1
    for (let index = labelEnd + 2; index < cell.length; index += 1) {
      const char = cell[index] ?? ""
      if (
        char === "\\" &&
        (cell[index + 1] === "(" || cell[index + 1] === ")")
      ) {
        destination += cell.slice(index, index + 2)
        index += 1
        continue
      }
      if (char === "(") {
        balance += 1
        destination += char
        continue
      }
      if (char === ")") {
        if (balance === 0) {
          return {
            index: start,
            length: index + 1 - start,
            label,
            destination: sawWhitespace ? "" : destination,
          }
        }
        balance -= 1
        if (balance === 0 && unterminatedEnd < 0) {
          unterminatedEnd = index
        }
        destination += char
        continue
      }
      if (/\s/.test(char)) {
        sawWhitespace = true
      }
      destination += char
    }
    if (unterminatedEnd >= 0) {
      // No `)` ever closed the construct, so the first `)` that closed an inner
      // pair is as far as the base path's `[^)]*` scan ever got. The destination
      // it leaves behind keeps an unbalanced `(` and can never parse as a link.
      return {
        index: start,
        length: unterminatedEnd + 1 - start,
        label,
        destination: "",
      }
    }
  }
  return null
}

/** Reduce inline emphasis and code to plain text so a cell never shows literal `**`. */
function reduceInlineMarkup(value: string): string {
  return value
    .replace(/(\*\*|__)(.+?)\1/g, "$2")
    .replace(/(^|[^*\w])[*_]([^*_]+)[*_](?=[^*\w]|$)/g, "$1$2")
    .replace(/`([^`]*)`/g, "$1")
}

/**
 * Reduce a table cell to the text the notebook stores. A cell is a plain string,
 * so a citation only survives the save if its destination stays in that string:
 * dropping the destination here is what silently turned a column of conversation
 * links into dead text. Emphasis and code are still flattened, and the
 * destination is left untouched by those passes so an id containing `_` or `*`
 * is not mangled.
 *
 * The label-only form is the fallback: it is used when the destination is
 * empty, carries whitespace, or does not parse back as a link at all, and when
 * keeping the destination would push the cell past the store's cap. Header
 * cells always take it (`keepDestinations: false`): a column name doubles as
 * every row's key and the notebook renders it as plain text.
 */
function plainCellText(cell: string, keepDestinations: boolean): string {
  // Code spans are flattened first so a citation Sofia wrapped in backticks
  // still becomes a preserved link instead of staying a code span the
  // entity-link plugins skip.
  const flattened = cell.replace(CODE_SPAN_RE, "$1")
  let linked = ""
  let labelled = ""
  let index = 0
  for (
    let match = matchCellLink(flattened, 0);
    match;
    match = matchCellLink(flattened, index)
  ) {
    const before = reduceInlineMarkup(flattened.slice(index, match.index))
    const label = reduceInlineMarkup(match.label).trim()
    const destination = match.destination.trim()
    const resolvable = destination.length > 0 && !/\s/.test(destination)
    linked += `${before}${resolvable ? `[${label}](${destination})` : label}`
    labelled += `${before}${label}`
    index = match.index + match.length
  }
  const tail = reduceInlineMarkup(flattened.slice(index))
  const withDestinations = `${linked}${tail}`.trim()
  if (keepDestinations && withDestinations.length <= MAX_CELL_CHARS) {
    return withDestinations
  }
  return reduceInlineMarkup(`${labelled}${tail}`).trim()
}

function splitRow(line: string, keepDestinations = true): string[] {
  const cells: string[] = []
  let cell = ""
  let backslashes = 0
  for (const character of line.trim()) {
    if (character === "\\") {
      cell += character
      backslashes += 1
      continue
    }
    if (character === "|") {
      if (backslashes % 2 === 1) {
        cell = `${cell.slice(0, -1)}|`
      } else {
        cells.push(cell)
        cell = ""
      }
      backslashes = 0
      continue
    }
    cell += character
    backslashes = 0
  }
  cells.push(cell)
  if (cells[0]?.trim() === "") {
    cells.shift()
  }
  if (cells.at(-1)?.trim() === "") {
    cells.pop()
  }
  return cells.map((value) => plainCellText(value, keepDestinations))
}

function isSeparatorRow(line: string): boolean {
  const cells = splitRow(line)
  return (
    cells.length > 0 &&
    cells.every((cell) => /^:?-{3,}:?$/.test(cell.replace(/\s/g, "")))
  )
}

function isTableRow(line: string): boolean {
  return splitRow(line).length > 1
}

function uniqueColumns(columns: string[]): string[] {
  const used = new Set<string>()
  return columns.map((column, index) => {
    const base = column || `Column ${index + 1}`
    let candidate = base
    let suffix = 2
    while (used.has(candidate)) {
      candidate = `${base} (${suffix})`
      suffix += 1
    }
    used.add(candidate)
    return candidate
  })
}

interface MarkdownFence {
  marker: "`" | "~"
  length: number
}

function openingFence(line: string): MarkdownFence | null {
  const match = /^\s{0,3}(`{3,}|~{3,})/.exec(line)
  const run = match?.[1]
  if (!run) {
    return null
  }
  return { marker: run[0] as MarkdownFence["marker"], length: run.length }
}

function closesFence(line: string, fence: MarkdownFence): boolean {
  const match = /^\s{0,3}(`+|~+)\s*$/.exec(line)
  const run = match?.[1]
  return Boolean(
    run && run.startsWith(fence.marker) && run.length >= fence.length,
  )
}

/**
 * Pull GitHub-flavored markdown tables out of Sofia's prose so a bounded
 * result can render as a table cell. Sofia only ever sees bounded tool
 * payloads, so the tables are already small; the caps here are a guard, not a
 * budget. `keepDestinations: false` stores every body cell label-only too.
 */
export function extractMarkdownTables(
  text: string,
  { keepDestinations = true }: { keepDestinations?: boolean } = {},
): MarkdownTableExtraction {
  const lines = text.split("\n")
  const tables: ExtractedTable[] = []
  const remainderLines: string[] = []
  let index = 0
  let fence: MarkdownFence | null = null
  while (index < lines.length) {
    const line = lines[index] ?? ""
    const next = lines[index + 1] ?? ""
    if (fence) {
      remainderLines.push(line)
      if (closesFence(line, fence)) {
        fence = null
      }
      index += 1
      continue
    }
    const openedFence = openingFence(line)
    if (openedFence) {
      fence = openedFence
      remainderLines.push(line)
      index += 1
      continue
    }
    if (isTableRow(line) && isSeparatorRow(next)) {
      const columns = uniqueColumns(
        splitRow(line, false).slice(0, MAX_TABLE_COLUMNS),
      )
      const rows: Record<string, string>[] = []
      index += 2
      while (index < lines.length && isTableRow(lines[index] ?? "")) {
        if (rows.length < MAX_TABLE_ROWS) {
          const cells = splitRow(lines[index] ?? "", keepDestinations)
          const row: Record<string, string> = {}
          columns.forEach((column, columnIndex) => {
            row[column] = cells[columnIndex] ?? ""
          })
          rows.push(row)
        }
        index += 1
      }
      if (columns.length > 0 && rows.length > 0) {
        tables.push({ columns, rows })
      }
      continue
    }
    remainderLines.push(line)
    index += 1
  }
  return {
    tables,
    remainder: remainderLines
      .join("\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim(),
  }
}
